import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import ChildProfile from './components/ChildProfile';
import Activities from './components/Activities';
import KnowledgeBase from './components/KnowledgeBase';
import ChatAssistant from './components/ChatAssistant';
import WeeklyReports from './components/WeeklyReports';
import LLMAnalytics from './components/LLMAnalytics';
import UserManagement from './components/UserManagement';
import ExpertRatings from './components/ExpertRatings';
import UserSettings from './components/UserSettings';
import EvaluationsDashboard from './components/EvaluationsDashboard';
import Memories from './components/Memories';
import ImportantMemories from './components/ImportantMemories';
import MemoryAnalytics from './components/admin/MemoryAnalytics';
import AIBackendDiagnostics from './components/AIBackendDiagnostics';
import Sidebar from './components/Sidebar';
import { Toaster } from './components/ui/sonner';

function ProtectedRoute({ children, adminOnly = false, superadminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (superadminOnly && !user.is_superadmin && user.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppContent() {
  const { user, loading } = useAuth();
  const { isCollapsed } = useSidebar();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <BrowserRouter>
        {user && <Sidebar />}
        <div className={user ? `pt-16 lg:pt-0 transition-all duration-300 ${isCollapsed ? 'lg:pl-20' : 'lg:pl-64'}` : ""}>
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/child/:childId"
              element={
                <ProtectedRoute>
                  <ChildProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/:childId"
              element={
                <ProtectedRoute>
                  <Activities />
                </ProtectedRoute>
              }
            />
            <Route
              path="/knowledge"
              element={
                <ProtectedRoute>
                  <KnowledgeBase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatAssistant />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:childId"
              element={
                <ProtectedRoute>
                  <WeeklyReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/memories"
              element={
                <ProtectedRoute>
                  <Memories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/memories/important"
              element={
                <ProtectedRoute>
                  <ImportantMemories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/llm-analytics"
              element={
                <ProtectedRoute adminOnly={true}>
                  <LLMAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/memory-analytics"
              element={
                <ProtectedRoute adminOnly={true}>
                  <MemoryAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/users"
              element={
                <ProtectedRoute superadminOnly={true}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/expert-ratings"
              element={
                <ProtectedRoute superadminOnly={true}>
                  <ExpertRatings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/evaluations"
              element={
                <ProtectedRoute superadminOnly={true}>
                  <EvaluationsDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/superadmin/ai-diagnostics"
              element={
                <ProtectedRoute superadminOnly={true}>
                  <AIBackendDiagnostics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <UserSettings />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <SidebarProvider>
        <AppContent />
      </SidebarProvider>
    </AuthProvider>
  );
}

export default App;
